import Button from '@carvana/ds-button';
import Clickable from '@carvana/merch-components/Clickable';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import cx from 'classnames';
import React, { useMemo, useRef } from 'react';
import { useIsMobile } from '~/context/common/DeviceContext';
import {
  INACTIVE_SLIDE_OPACITY,
  SlideContentWrapper
} from './MarketingCarousel.styles';

type SlideContentProps = {
  className?: string;
  heading: string;
  subHeading: string;
};

function SlideContent(props: SlideContentProps) {
  return (
    <div className={cx(props.className, 'flex flex-col gap-y-lg')}>
      <h2 className="t-heading-md sm:t-heading-xl">{props.heading}</h2>
      <div className="grid t-body-sm">
        <p className="col-span-full row-span-full">{props.subHeading}</p>
      </div>
    </div>
  );
}

type OwnProps = {
  backgroundColor: string;
  cta: {
    action: string | ((ev: React.MouseEvent) => void);
    nativePayload?: string;
    text: string;
  };
  heading: string;
  image: {
    alt: string;
    desktop: string;
    mobile: string;
    tablet: string;
  };
  opacity: number;
  spacerHeading: string;
  spacerSubHeading: string;
  mobileImageHeight?: string;
  subHeading: string;
};

export type { OwnProps as SlideProps };

export default function Slide(props: OwnProps) {
  const isMobile = useIsMobile();
  const dragStartRef = useRef(0);
  const hasMobileImageHeight = props.mobileImageHeight && isMobile;

  const SlideButton = useMemo(
    () =>
      (
        clickProps: React.HTMLAttributes<HTMLElement> & {
          'data-analytics-id': string;
        }
      ) =>
        typeof props.cta.action === 'string' ? (
          <a
            href={props.cta.action}
            data-native-payload={props.cta.nativePayload}
            data-testid="marketing-slide-link"
            {...clickProps}
          />
        ) : (
          <Clickable
            onMouseDown={(ev: React.MouseEvent) => {
              dragStartRef.current = ev.clientX;
            }}
            onTouchStart={(ev: React.TouchEvent) => {
              dragStartRef.current = ev.touches[0].clientX;
            }}
            onClick={ev => {
              if (
                typeof props.cta.action === 'function' &&
                Math.abs(ev.clientX - dragStartRef.current) < 5
              ) {
                props.cta.action(ev);
              }
            }}
            data-testid="marketing-slide-button"
            {...clickProps}
          />
        ),
    [props.cta]
  );

  return (
    <SlideButton
      draggable={false}
      data-analytics-id="Carousel Slide"
      data-analytics-attributes={JSON.stringify({
        text: props.cta.text
      })}
      className={cx(
        'grid overflow-hidden rounded-xl duration-[400ms] sm:transition-opacity',
        'bg-[var(--background-color)]'
      )}
      style={
        {
          '--background-color': props.backgroundColor,
          opacity: props.opacity
        } as React.CSSProperties
      }
    >
      <picture className="pointer-events-none flex w-full md:col-span-full md:row-span-full">
        <source
          media={`(min-width: ${breakpoints.md}px)`}
          srcSet={props.image.desktop}
        />
        <source
          media={`(min-width: ${breakpoints.sm}px)`}
          srcSet={props.image.tablet}
        />
        <img
          src={props.image.mobile}
          alt={props.image.alt}
          className={cx(
            'w-full',
            hasMobileImageHeight && `${props.mobileImageHeight} object-cover`
          )}
          loading="lazy"
        />
      </picture>

      <SlideContentWrapper
        style={{
          // Normalize opacity from [INACTIVE_SLIDE_OPACITY, 1] to [0, 1]
          // so inactive slide content fades to transparent while the slide
          // container itself remains at INACTIVE_SLIDE_OPACITY
          opacity: Math.max(
            0,
            (props.opacity - INACTIVE_SLIDE_OPACITY) /
              (1 - INACTIVE_SLIDE_OPACITY)
          )
        }}
      >
        <div className="grid">
          <SlideContent
            className="col-span-full row-span-full justify-end"
            heading={props.heading}
            subHeading={props.subHeading}
          />
          {/* Render spacer content so all slides are the same height */}
          {!hasMobileImageHeight && (
            <SlideContent
              className={cx(
                'col-span-full row-span-full',
                'user-select-none pointer-events-none invisible'
              )}
              heading={props.spacerHeading}
              subHeading={props.spacerSubHeading}
            />
          )}
        </div>

        <Button
          variant="secondaryInverted"
          size={isMobile ? 'sm' : 'md'}
          className="sm:w-fit"
        >
          {props.cta.text}
        </Button>
      </SlideContentWrapper>
    </SlideButton>
  );
}
